import { NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { LoadingBarHttpClientModule } from "@ngx-loading-bar/http-client";
import { LoadingBarModule } from "@ngx-loading-bar/core";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import { HttpClientModule, HTTP_INTERCEPTORS} from "@angular/common/http";
import { GoogleMapsModule } from "@angular/google-maps";
import { JwtModule, JWT_OPTIONS } from "@auth0/angular-jwt";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { CouponModule } from "./controller/coupon/coupon.module";
import { HeaderComponent } from "./controller/header/header.component";
import { FooterComponent } from "./controller/footer/footer.component";
import { SharedModule } from "./shared/shared.module";
import { HeroSliderComponent } from "./controller/fold/hero-slider/hero-slider.component";
import { EarnWellBuxFoldComponent } from "./controller/fold/earn-well-bux-fold/earn-well-bux-fold.component";
import { BenefitsOfMerchantFoldComponent } from "./controller/fold/benefits-of-merchant-fold/benefits-of-merchant-fold.component";
import { AppFeaturesFoldComponent } from "./controller/fold/app-features-fold/app-features-fold.component";
import { AboutWellbuxVedioFoldComponent } from "./controller/fold/about-wellbux-vedio-fold/about-wellbux-vedio-fold.component";
import { LoginModule } from "./controller/login/login.module";
import { SignUpModule } from "./controller/sign-up/sign-up.module";
import { SpecialSaleModule } from "./controller/special-sale/special-sale.module";
import { NgxSpinnerModule } from "ngx-spinner";
import { FrontFactoryDirective } from "./util/directive/front-factory.directive";
import { CreditCardDirectivesModule } from "angular-cc-library";
import { ManageChallengeCategoriesModule } from "./controller/manage-challenge-categories/manage-challenge-categories.module";
import { ManageMapModule } from "./controller/manage-map/manage-map.module";
import { UserHeaderComponent } from './user/controller/user-header/user-header.component';

// Used for notifiacation toaster
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { ToastrModule } from "ngx-toastr";

// This for internationalization language.
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";

// Import Custom pipes module
// Import globals file
import { Globals } from "./globals/globals";
import { UserModelService } from "./model/user-model/user-model.service";
import { LocaleModelService } from "./model/locale-model/locale-model.service";
import { environment } from "../environments/environment";
import { CustomFieldModelService } from "./model/custom-field-model/custom-field-model.service";
import { TeamModelService } from "./model/team-model/team-model.service";
import { CommunityModelService } from "./model/community-model/community-model.service";
import { CouponService } from "./model/coupon/coupon.service";
import { ReportModelService } from "./model/report-model/report-model.service";
import { TrackerService } from "./model/tracker-model/tracker.service";
import { ChallengesService } from "./model/challenges/challenges.service";
import { AuthGuard } from "./auth-guard/auth.guard";
import { CommonModule } from "@angular/common";
import { ConfirmationDialogComponent } from './controller/confirmation-dialog/confirmation-dialog.component';
import { from, Observable } from "rxjs";
import { pluck } from "rxjs/operators";
import { TokenService } from "./util/service/token.service";
import { AuthInterceptor } from "./util/middleware/auth-interceptor";
import { RemoveSpacePipe } from "./util/pipe/removeSpace.pipe";
import { UnitService } from "./model/unit-model/unit.service";
import { NotificationModelService } from "./model/notification-model/notification-model.service";
import { UserSignUpComponent } from "./user/controller/user-sign-up/user-sign-up.component";
import { UsersLoginComponent } from "./user/controller/users-login/users-login.component";
import { MerchantSignupComponent } from "./user/controller/merchant-signup/merchant-signup.component";
import { CustomToastMessageComponent } from "./user/shared/components/custom-toast-component/custom-toast-message/custom-toast-message.component";
import { SocialSignUpComponent } from "./user/controller/social-sign-up/social-sign-up.component";
import { CustomConfirmationPopupComponent } from "./user/shared/components/custom-confirmation-popup-component/custom-confirmation-popup.component";
import { UserProfileComponent } from "./user/controller/user-profile/user-profile.component";
import { UserContactComponent } from "./user/controller/user-contact/user-contact.component";

const authUrl: string = environment.apiUrl;
const toasterSetting: any = environment.toasterSetting;
const googleMapSetting: any = environment.googleMapSetting;

// Jwt Option Data. We can set URL in BlackList(Not allowed) or in WhiteList(allowed)
export function jwtOptionsFactory(TokenService) {
  return {
    tokenGetter: () => {
      return TokenService.getToken();
    },
    whitelistedDomains: [authUrl],
  };
}

export class WebpackTranslateLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<any> {
    return from(import(`../assets/i18n/${lang}.ts`)).pipe(pluck('default'));    
  }
}

@NgModule({
    declarations: [
        AppComponent,
        HeaderComponent,
        FooterComponent,
        HeroSliderComponent,
        EarnWellBuxFoldComponent,
        BenefitsOfMerchantFoldComponent,
        AppFeaturesFoldComponent,
        AboutWellbuxVedioFoldComponent,
        FrontFactoryDirective,
        ConfirmationDialogComponent,
        RemoveSpacePipe
        
    ],
    imports: [
        CommonModule,
        BrowserModule,
        FormsModule,
        CustomToastMessageComponent,  
        CreditCardDirectivesModule,
        UserSignUpComponent,
        UserProfileComponent,
        ReactiveFormsModule,
        HttpClientModule,
        LoadingBarModule,
        LoadingBarHttpClientModule,
        SharedModule,
        UsersLoginComponent,
        MerchantSignupComponent,
        SocialSignUpComponent,
        UserContactComponent,
        AppRoutingModule,
        CouponModule,
        SpecialSaleModule,
        LoginModule,
        BrowserAnimationsModule,
        NgxSpinnerModule,
        ManageChallengeCategoriesModule,
        ManageMapModule,
        ToastrModule.forRoot(toasterSetting),
        JwtModule.forRoot({
            jwtOptionsProvider: {
                provide: JWT_OPTIONS,
                useFactory: jwtOptionsFactory,
                deps: [TokenService],
            },
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: WebpackTranslateLoader
            }
        }),
        GoogleMapsModule,
        SignUpModule,
        UserHeaderComponent,
        CustomToastMessageComponent,
        CustomConfirmationPopupComponent,
    ],
    providers: [
        Globals,
        UserModelService,
        LocaleModelService,
        CustomFieldModelService,
        TeamModelService,
        CommunityModelService,
        CouponService,
        ReportModelService,
        TrackerService,
        UnitService,
        AuthGuard,
        ChallengesService,
        NotificationModelService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        }
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }
